import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import type { User, Role } from "./auth.types";
import { BACKEND_URL } from "../../config";
import { normalizeRole, userHasPermission, type Permission } from "./accessControl";

axios.defaults.withCredentials = true; // important for OAuth + sessions

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  hasRole: (roles: Role[] | Role) => boolean;
  can: (permission: Permission | Permission[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const LOCAL_KEY = "clinic_user_v1";

const normalizeUser = (data: any): User => {
  const sourceRoles = data.roles?.length ? data.roles : [data.role || "viewer"];
  const roleLabels = data.roleLabels?.length ? data.roleLabels : sourceRoles;
  const roles = sourceRoles.map(normalizeRole);
  return {
    ...data,
    name: data.name || data.displayName || data.email,
    role: roles[0] || "viewer",
    roles,
    roleLabel: data.roleLabel || roleLabels[0] || roles[0] || "viewer",
    roleLabels,
  };
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(() => {
    try {
      const data = localStorage.getItem(LOCAL_KEY);
      return data ? normalizeUser(JSON.parse(data)) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  const saveUser = (nextUser: User | null) => {
    setUser(nextUser);
    if (nextUser) {
      localStorage.setItem(LOCAL_KEY, JSON.stringify(nextUser));
    } else {
      localStorage.removeItem(LOCAL_KEY);
    }
  };

// Restore session from backend (Google / email login)
useEffect(() => {
  const restore = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/me`, {
        withCredentials: true,
      });

      if (res.data.loggedIn) {
        saveUser(normalizeUser(res.data.user));
      } else {
        saveUser(null);
      }
    } catch {
      saveUser(null);
    } finally {
      setLoading(false);
    }
  };

  restore();
}, []);
  // -------------------------
  // AUTH METHODS
  // -------------------------

  const login = async (email: string, password: string) => {
    const res = await axios.post(
      `${BACKEND_URL}/auth/login`,
      { email, password },
      { withCredentials: true }
    );

    if (!res.data || !res.data.user) {
      throw new Error("Invalid login response from server");
    }

    saveUser(normalizeUser(res.data.user));
  };

  const logout = async () => {
    await axios.post(
      `${BACKEND_URL}/api/logout`,
      {},
      { withCredentials: true }
    );
    saveUser(null);
  };

  const hasRole = (roles: Role[] | Role) => {
    if (!user) return false;
    const userRoles = user.roles?.length ? user.roles : [user.role];
    if (Array.isArray(roles)) return roles.some((role) => userRoles.includes(role));
    return userRoles.includes(roles);
  };

  const can = (permission: Permission | Permission[]) => userHasPermission(user, permission);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        hasRole,
        can,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
